/**
 * App shell and navigation.
 *
 * Bottom tabs on mobile — thumb-first, like the day sheet. Three destinations is
 * the right number for a tab bar; when Settings and Community arrive it stays
 * four or five, and Community only exists when enabled (FR-902).
 *
 * The quick-exit control (FR-113) belongs in this header and lands in v0.4,
 * where the lock screen it depends on also arrives.
 */
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { t } from '@copy/index'
import { useLock } from '@contexts/index'
import { StorageNotices } from './StorageNotices'
import { UpdateNotice } from './UpdateNotice'
import { InstallNotice } from './InstallNotice'


const TABS = [
  { to: '/', key: 'calendar' },
  { to: '/today', key: 'today' },
  { to: '/insights', key: 'insights' },
  { to: '/data', key: 'data' },
] as const

function TabIcon({ name, className }: { name: 'calendar' | 'today' | 'insights' | 'data'; className?: string }) {
  if (name === 'calendar') {
    return (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
      </svg>
    )
  }
  if (name === 'today') {
    return (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m0-12.728l.707.707m11.314 11.314l.707.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
      </svg>
    )
  }
  if (name === 'insights') {
    return (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125z" />
      </svg>
    )
  }
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 5.625c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
    </svg>
  )
}

export function Layout() {
  const { quickExit, phase, lockNow } = useLock()
  const navigate = useNavigate()

  return (
    <div className="mx-auto flex h-full max-w-md flex-col">
      {/* Header controls: lock/setup on the left, quick exit on the right */}
      <div className="flex justify-between px-4 pt-2">
        {phase === 'unlocked' ? (
          <button
            type="button"
            onClick={lockNow}
            aria-label={t('nav.lockApp')}
            title={t('nav.lockAppTooltip')}
            className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-sm text-ink-muted transition-all duration-200 hover:bg-line/40 hover:text-ink active:scale-90"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigate('/data#protection')}
            aria-label={t('nav.setupPin')}
            title={t('nav.setupPinTooltip')}
            className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-sm text-ink-muted transition-all duration-200 hover:bg-line/40 hover:text-ink active:scale-90"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0 1 9.9-1" />
            </svg>
          </button>
        )}

        <button
          type="button"
          onClick={quickExit}
          aria-label={t('nav.quickExit')}
          title={t('nav.quickExitTooltip')}
          className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-sm text-ink-muted transition-all duration-200 hover:bg-line/40 hover:text-ink active:scale-90"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
          </svg>
        </button>
      </div>

      {/* Below the storage notices on purpose: a database that is not saving
          anything matters more than a version that is one release behind. */}
      <StorageNotices />
      <UpdateNotice />
      <InstallNotice />


      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>

      <nav aria-label={t('nav.label')} className="border-t border-line">
        <ul className="flex">
          {TABS.map((tab) => (
            <li key={tab.key} className="flex-1">
              <NavLink
                to={tab.to}
                end={tab.to === '/'}
                className={({ isActive }) =>
                  [
                    'flex min-h-14 flex-col items-center justify-center gap-1 text-[10px] transition-all duration-200',
                    // aria-current carries the state for assistive tech; the
                    // weight and colour are for everyone else. Never colour
                    // alone (NFR-10).
                    isActive ? 'font-semibold text-ink' : 'text-ink-muted hover:text-ink',
                  ].join(' ')
                }
              >
                <TabIcon name={tab.key} className="h-5 w-5" />
                <span>{t(`nav.${tab.key}`)}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}
